import { useState, useEffect, useContext } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Navbar,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { PersonFill, LockFill } from "react-bootstrap-icons";

import { signInUser } from "./CustomerLoginPageApi";
import InvalidCredentialsModal from "./components/InvalidCredentialsModal";

import loginPageImg from "../../components/Assests/Icons/login-page-img.png";
import phibroLogo from "../../components/Assests/Phibro-Tagline-Logo.png";
import { getRole } from "../../services/signInApi";

import "./styles/customerSignIn.css";
import { RoleContext } from "../../RoleContext";

const CustomerLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  

  const { setRole, setRoleOptions } = useContext(RoleContext);
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "email") {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }));
    } else if (field === "password") {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }));
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "email") {
      setEmail(value);
      if (errors.email) setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (field === "password") {
      setPassword(value);
      if (errors.password) setErrors((prev) => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const validateForm = () => {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    setErrors({ email: emailError, password: passwordError });
    return !emailError && !passwordError;
  };

  const handleSignIn = async () => {
    if (!validateForm()) {
      setTouched({ email: true, password: true });
      return;
    }
    setIsLoading(true);
    try {
      const response = await signInUser({
        userEmail: email,
        password: password,
      });

      if (response?.success && response?.token) {
        // choose storage based on rememberMe: localStorage if remembered, otherwise sessionStorage
        const storage = rememberMe ? window.localStorage : window.sessionStorage;
        const otherStorage = rememberMe ? window.sessionStorage : window.localStorage;

        storage.setItem("accessToken", response.token);
        storage.setItem("tokenType", response.tokenType || "Bearer");
        storage.setItem("tokenExpiresIn", response.expiresIn?.toString() || "28800");

        const expirationTime = new Date().getTime() + (Number(response.expiresIn) || 28800) * 1000;
        storage.setItem("tokenExpiration", expirationTime.toString());

        // Set login type to distinguish between MSAL and custom login
        storage.setItem("loginType", "custom");

        const userData = await getRole(response?.token);
        userData.roleName = ["Customer"];
        storage.setItem("userData", JSON.stringify(userData));

        storage.setItem("currentRole", JSON.stringify("Customer"));


        // remove any leftover values from the other storage so there's no stale token
        try {
          otherStorage.removeItem("accessToken");
          otherStorage.removeItem("tokenType");
          otherStorage.removeItem("tokenExpiresIn");
          otherStorage.removeItem("tokenExpiration");
          otherStorage.removeItem("userData");
          otherStorage.removeItem("currentRole");
          otherStorage.removeItem("loginType");
        } catch (err) {
          // ignore if storage access fails
        }
        
        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("currentRole", JSON.stringify("Customer"));
        
        setRoleOptions(userData.roleName || []);
        setRole(userData.roleName?.[0] || "");

        // continue to store the userEmail in localStorage only when rememberMe is true
        if (rememberMe) {
          localStorage.setItem("userEmail", email);
        } else {
          localStorage.removeItem("userEmail");
        }

        navigate("/customer-dashboard");
      } else {
        setShowModal(true);
      }
    } catch (error) {
      console.error("Login error:", error);
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const shouldShowError = (fieldName) => errors[fieldName] && touched[fieldName];

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        backgroundImage: `url(${loginPageImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Navbar bg="white" className="px-3" style={{ borderBottom: "4px solid #0070c0", minHeight: 60 }}>
        <Navbar.Brand href="#" className="d-flex align-items-center gap-2">
          <img src={phibroLogo} alt="Phibro Logo" style={{ height: 40 }} />
          <span style={{ fontWeight: 500, fontSize: 22, color: "#003087" }}>
            Feed Assay Portal
          </span>
        </Navbar.Brand>
      </Navbar>

      {/* Main Content */}
      <Container className="signin-container d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
        <Card className="signin-card">
          <Card.Body>
            <h3 className="text-center mb-4">Sign In</h3>
            <Form>
              <Form.Group className="mb-3">
                <InputGroup>
                  <InputGroup.Text className="input-icon"><PersonFill /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email address"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    className={shouldShowError("email") ? "is-invalid" : ""}
                  />
                </InputGroup>
                {shouldShowError("email") && <div className="invalid-feedback d-block">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="mb-3">
                <InputGroup>
                  <InputGroup.Text className="input-icon"><LockFill /></InputGroup.Text>
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    onBlur={() => handleBlur("password")}
                    className={shouldShowError("password") ? "is-invalid" : ""}
                  />
                </InputGroup>
                {shouldShowError("password") && <div className="invalid-feedback d-block">{errors.password}</div>}
              </Form.Group>

              <Form.Group className="d-flex justify-content-between align-items-center mb-3">
                <Form.Check
                  type="checkbox"
                  label="Remember Me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                {/* <span onClick={() => navigate("/faqs")} className="link-text clickable-link">
                  FAQs
                </span> */}
              </Form.Group>

              {/* captcha removed */}

              <Button
                variant="primary"
                className="signin-btn w-100 mb-3"
                onClick={handleSignIn}
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </Button>

              <Button
                variant="success"
                className="create-account-btn w-100 mb-3"
                onClick={() => navigate("/registrationForm")}
              >
                Don't have an Account?
              </Button>

              <Row className="text-center">
                <Col>
                  <span onClick={() => navigate("/forgot-Password")} className="link-text clickable-link">
                    Forgot Password?
                  </span>
                </Col>
                <Col>
                  <span onClick={() => navigate("/update-password")} className="link-text clickable-link">
                    Have a Verification Code?
                  </span>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        <InvalidCredentialsModal show={showModal} onHide={() => setShowModal(false)} />
      </Container>

      {/* Footer */}
      <Navbar variant="dark" className="justify-content-center footer-style" style={{ minHeight: 40 }}>
        <span className="text-white" style={{ fontSize: 14 }}>
          © 2025 Phibro Animal Health Corporation. All rights reserved.
        </span>
      </Navbar>
    </div>
  );
};

export default CustomerLoginPage;
